import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, Users, Contact, Landmark, FileText, ArrowRight, Calendar, Newspaper, Trophy } from "lucide-react";
import { LINK_ICONS } from "@/constants/linkIcons";
const EVENT_COLORS = {
  Trial: "bg-primary/15 text-primary", Hearing: "bg-blue-500/15 text-blue-500",
  CLE: "bg-emerald-500/15 text-emerald-500", Deadline: "bg-amber-500/15 text-amber-600",
  "Firm Event": "bg-purple-500/15 text-purple-500",
};

function fmtDate(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return d; }
}

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const counts = data?.counts || {};
  const stats = [
    { label: "People", value: counts.staff, to: "/people", icon: Users },
    { label: "Vendors & Experts", value: counts.vendors, to: "/vendors", icon: Contact },
    { label: "Courts Mapped", value: counts.courts, to: "/courts", icon: Landmark },
    { label: "Knowledge Docs", value: (counts.documents || 0) + (counts.articles || 0), to: "/brief-bank", icon: FileText },
  ];

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-brand-navy-grad text-white rounded-sm p-8 lg:p-10 animate-fade-up">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(-45deg,#fff 0,#fff 1px,transparent 1px,transparent 24px)" }} />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-red-grad opacity-25 blur-3xl" />
        <div className="relative">
          <img src="/monge-logo-footer.svg" alt="Monge & Associates" className="h-7 w-auto mb-5 opacity-95" />
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-primary mb-3">
            <Trophy className="h-4 w-4" /> The Hub · Attorneys Who Win
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">
            Welcome back, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-white/60 mt-2 max-w-xl text-sm">
            Everything the firm knows, in one secured place.
          </p>
        </div>
      </div>

      {/* Today quick links */}
      <section>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><span className="h-4 w-1 rounded-full bg-brand-red-grad" /><Clock className="h-4 w-4 text-primary" /> Today · Quick Links</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="quicklinks">
          {(data?.quicklinks || []).map((l) => {
            const Icon = LINK_ICONS[l.icon] || ArrowRight;
            return (
              <a key={l.id} href={l.url} target={l.url?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                data-testid="quicklink" className="group border border-border bg-card p-4 rounded-sm hover:border-primary hover:shadow-md transition-all">
                <span className="inline-grid place-items-center h-9 w-9 rounded-sm bg-primary/10 text-primary mb-3 group-hover:bg-brand-red-grad group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="text-sm font-medium leading-tight group-hover:text-primary transition-colors">{l.label}</div>
              </a>
            );
          })}
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={`stat-${s.label}`} className="relative overflow-hidden border border-border bg-card p-5 rounded-sm hover:border-primary hover:shadow-md transition-all group">
            <span className="absolute top-0 left-0 right-0 h-0.5 bg-brand-red-grad opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-center justify-between">
              <span className="text-4xl font-condensed font-extrabold tracking-tight">{s.value ?? "—"}</span>
              <s.icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{s.label}</div>
          </Link>
        ))}
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* News */}
        <section className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><span className="h-4 w-1 rounded-full bg-brand-red-grad" /><Newspaper className="h-4 w-4 text-primary" /> Firm News</h2>
            <Link to="/news" className="text-xs text-primary hover:underline flex items-center gap-1">All news <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="space-y-3" data-testid="home-news">
            {(data?.news || []).map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} className="block border border-border bg-card p-4 rounded-sm hover:border-primary transition-colors">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm">{n.category}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(n.date)}</span>
                </div>
                <h3 className="font-semibold text-sm">{n.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{n.body}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Upcoming */}
        <section>
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><span className="h-4 w-1 rounded-full bg-brand-red-grad" /><Calendar className="h-4 w-4 text-primary" /> Upcoming</h2>
          <div className="border border-border bg-card rounded-sm divide-y divide-border" data-testid="home-events">
            {(data?.events || []).map((e) => (
              <div key={e.id} className="p-3.5 flex items-start gap-3">
                <div className="text-center shrink-0 w-11">
                  <div className="text-[10px] uppercase text-muted-foreground">{fmtDate(e.date).split(" ")[0]}</div>
                  <div className="text-lg font-display font-bold leading-none">{fmtDate(e.date).split(" ")[1]}</div>
                </div>
                <div className="min-w-0">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${EVENT_COLORS[e.type] || "bg-muted"}`}>{e.type}</span>
                  <div className="text-sm font-medium mt-1 truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground">{e.time} · {e.location}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
